import { AdvisorInfo } from './advisor-info.model';
import { Json } from '@/integrations/supabase/types';

/**
 * Status possibili per una travel request
 */
export type TravelRequestStatus = 'pending' | 'assigned' | 'completed' | 'rejected';

/**
 * Informazioni guest
 */
export interface IGuest {
  id: number;
  age: number;
}

/**
 * Informazioni di contatto del cliente
 */
export interface IContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** Foto profilo, NULL per i contatti senza account (richieste anonime). */
  avatarUrl?: string | null;
}

/**
 * Dati raw che arrivano dalla RPC get_dashboard_data per travel_requests
 */
export interface IDashboardTravelRequestData {
  // Identificatori
  id: string;
  user_id: string | null;
  
  // Dettagli viaggio
  destinations: string[];
  departure_point: string | null;
  needs_transportation: boolean;
  total_guests: number;
  guests_data: Json;
  travel_dates_flexible: boolean;
  travel_start_date: string | null;
  travel_end_date: string | null;
  budget_min: number | null;
  budget_max: number | null;
  budget_flexible: boolean;
  interests: string[];
  additional_notes: string | null;
  
  // Contatto
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  
  // Status e assegnazione
  status: string;
  assigned_designer_id: string | null;
  travel_designer_preference: string | null;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  
  // Travel Advisor info (from JOIN)
  td_first_name?: string | null;
  td_last_name?: string | null;
  td_email?: string | null;
  td_avatar_url?: string | null;
  contact_avatar_url?: string | null;
  
  // Preferred Advisor info (from JOIN)
  pref_first_name?: string | null;
  pref_last_name?: string | null;
  pref_email?: string | null;
}

/**
 * Interfaccia per DashboardTravelRequest
 */
export interface IDashboardTravelRequest {
  // Identificatori
  id: string;
  userId: string | null;
  
  // Dettagli viaggio
  destinations: string[];
  departurePoint: string | null;
  needsTransportation: boolean;
  totalGuests: number;
  guestsData: IGuest[];
  travelDatesFlexible: boolean;
  travelStartDate: string | null;
  travelEndDate: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetFlexible: boolean;
  interests: string[];
  additionalNotes: string | null;
  
  // Contatto
  contact: IContactInfo;
  
  // Status e assegnazione
  status: TravelRequestStatus;
  assignedAdvisorId: string | null;
  preferredAdvisorId: string | null;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Advisor info
  assignedAdvisor: AdvisorInfo | null;
  preferredAdvisor: AdvisorInfo | null;
}

/**
 * Classe DashboardTravelRequest
 * Rappresenta una travel request con advisor info embedded
 */
export class DashboardTravelRequest implements IDashboardTravelRequest {
  // Identificatori
  id: string;
  userId: string | null;
  
  // Dettagli viaggio
  destinations: string[];
  departurePoint: string | null;
  needsTransportation: boolean;
  totalGuests: number;
  guestsData: IGuest[];
  travelDatesFlexible: boolean;
  travelStartDate: string | null;
  travelEndDate: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetFlexible: boolean;
  interests: string[];
  additionalNotes: string | null;
  
  // Contatto
  contact: IContactInfo;
  
  // Status e assegnazione
  status: TravelRequestStatus;
  assignedAdvisorId: string | null;
  preferredAdvisorId: string | null;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Advisor info
  assignedAdvisor: AdvisorInfo | null;
  preferredAdvisor: AdvisorInfo | null;

  constructor(data: IDashboardTravelRequestData) {
    this.id = data.id;
    this.userId = data.user_id;
    
    // Dettagli viaggio
    this.destinations = data.destinations || [];
    this.departurePoint = data.departure_point;
    this.needsTransportation = data.needs_transportation ?? false;
    this.totalGuests = data.total_guests ?? 0;
    this.guestsData = this.parseGuestsData(data.guests_data);
    this.travelDatesFlexible = data.travel_dates_flexible ?? false;
    this.travelStartDate = data.travel_start_date;
    this.travelEndDate = data.travel_end_date;
    this.budgetMin = data.budget_min;
    this.budgetMax = data.budget_max;
    this.budgetFlexible = data.budget_flexible ?? false;
    this.interests = data.interests || [];
    this.additionalNotes = data.additional_notes;
    
    // Contatto
    this.contact = {
      firstName: data.contact_first_name,
      lastName: data.contact_last_name,
      email: data.contact_email,
      phone: data.contact_phone,
      avatarUrl: data.contact_avatar_url ?? null,
    };
    
    // Status e assegnazione
    this.status = data.status as TravelRequestStatus;
    this.assignedAdvisorId = data.assigned_designer_id;
    this.preferredAdvisorId = data.travel_designer_preference;
    
    // Timestamps
    this.createdAt = data.created_at;
    this.updatedAt = data.updated_at;
    
    // Map assigned advisor
    if (data.td_first_name && data.td_last_name && data.td_email) {
      this.assignedAdvisor = new AdvisorInfo({
        id: data.assigned_designer_id || undefined,
        firstName: data.td_first_name,
        lastName: data.td_last_name,
        email: data.td_email,
        avatarUrl: data.td_avatar_url ?? null,
      });
    } else {
      this.assignedAdvisor = null;
    }

    // Map preferred advisor
    if (data.pref_first_name && data.pref_last_name && data.pref_email) {
      this.preferredAdvisor = new AdvisorInfo({
        id: data.travel_designer_preference || undefined,
        firstName: data.pref_first_name,
        lastName: data.pref_last_name,
        email: data.pref_email,
      });
    } else {
      this.preferredAdvisor = null;
    }
  }

  /**
   * Helper per parsare guests_data da Json
   */
  private parseGuestsData(data: Json): IGuest[] {
    if (Array.isArray(data)) {
      return data as unknown as IGuest[];
    }
    return [];
  }

  // ============ COMPUTED PROPERTIES ============

  /**
   * Nome completo del contatto
   */
  get contactFullName(): string {
    return `${this.contact.firstName} ${this.contact.lastName}`.trim();
  }

  /**
   * Email del contatto (shorthand)
   */
  get contactEmail(): string {
    return this.contact.email;
  }

  /**
   * Telefono del contatto (shorthand)
   */
  get contactPhone(): string {
    return this.contact.phone;
  }

  /**
   * Nome completo del travel advisor assegnato
   */
  get assignedAdvisorFullName(): string | null {
    return this.assignedAdvisor?.fullName || null;
  }

  /**
   * Nome completo del travel advisor preferito
   */
  get preferredAdvisorFullName(): string | null {
    return this.preferredAdvisor?.fullName || null;
  }

  /**
   * Check se la request è in stato pending
   */
  get isPending(): boolean {
    return this.status === 'pending';
  }

  /**
   * Check se la request è scaduta (pending da più di 48 ore)
   */
  get isExpired(): boolean {
    if (this.status !== 'pending') return false;
    const now = new Date();
    const created = new Date(this.createdAt);
    const hoursSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return hoursSinceCreation > 48;
  }

  /**
   * Check se la request è assegnata
   */
  get isAssigned(): boolean {
    return this.status === 'assigned';
  }

  /**
   * Check se la request è completata
   */
  get isCompleted(): boolean {
    return this.status === 'completed';
  }

  /**
   * Check se la request è rifiutata
   */
  get isRejected(): boolean {
    return this.status === 'rejected';
  }

  /**
   * Check se ci sta lavorando qualcuno (assigned o completed)
   */
  get workingOn(): boolean {
    return this.isAssigned || this.isCompleted;
  }

  /**
   * Check se l'advisor assegnato è quello preferito
   */
  get hasPreferredAdvisorAssigned(): boolean {
    if (!this.assignedAdvisor || !this.preferredAdvisor) return false;
    return this.assignedAdvisor.email === this.preferredAdvisor.email;
  }

  /**
   * Numero totale di giorni del viaggio
   */
  get totalDays(): number {
    if (!this.travelStartDate || !this.travelEndDate) return 0;
    const start = new Date(this.travelStartDate);
    const end = new Date(this.travelEndDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  /**
   * Prima destinazione o stringa vuota
   */
  get primaryDestination(): string {
    return this.destinations[0] || '';
  }

  /**
   * Titolo formattato per la visualizzazione
   */
  get displayTitle(): string {
    return this.destinations.length > 0 
      ? this.destinations.join(', ') 
      : 'Destinazione da definire';
  }
}
